import React, { FC, useEffect, useState } from "react";

import * as Sentry from "@sentry/react";

import { Collapse, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import styled from "@mui/material/styles/styled";

import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import AdvancedSettingsModal from "@app/analytics/onboarding/steps/CalendarStep/AdvancedSettingsModal";
import EditableSettings from "@app/analytics/onboarding/steps/CalendarStep/EditableSettings";
import { PREVIEW_SETTINGS } from "@app/analytics/onboarding/steps/CalendarStep/settings";
import { toast } from "@app/common/hooks/useToasts";
import logger from "@app/utils/logger";

import { Column, Row, StackProps } from "@rui/Stack";
import Switch from "@rui/Switch";

const Wrapper = styled<FC<StackProps>>((props: StackProps) => (
  <Column spacing="16px" {...props} />
))(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.shades.neutral[100]}`,
  paddingTop: "24px",
}));

const Content = styled((props: StackProps) => <Column {...props} />)(
  ({ theme }) => ({
    border: `1px solid ${theme.palette.shades.neutral[100]}`,
    borderRadius: "8px",
    padding: "12px 16px",
  })
);

const CalendarStepSettingsRss = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const { hasConnectedCalendarProviders, isLoading } = useConnectedProviders();
  const { userSettings, updateUserSettings } = useUserSettings();
  const isInviteOnly = userSettings.calendarPermissionLevel === "invite_only";
  const [isMeetingsEnabled, setIsMeetingsEnabled] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    setIsMeetingsEnabled(hasConnectedCalendarProviders && !isInviteOnly);
  }, [isLoading]);

  useEffect(() => {
    if (hasConnectedCalendarProviders || isInviteOnly) {
      logger.debug("Skipping silent update for calendar permission level");
      return;
    }

    // if coming from RSS, we need to ensure that any connected calendars
    // default to "invite_only"; the next step will ask users to opt in
    // to meeting measurement and update their preferences accordingly.
    // don't take any action on success or failure since this should be a
    // silent update
    updateUserSettings.mutate(
      { calendarPermissionLevel: "invite_only" },
      {
        onSuccess: () => {
          logger.debug(
            "Successfully updated calendar permission level to invite_only"
          );
        },
        onError: (err) => Sentry.captureException(err),
      }
    );
  }, [hasConnectedCalendarProviders, isInviteOnly]);

  const handleChange = () => {
    const calendarPermissionLevel = isMeetingsEnabled ? "invite_only" : "all";
    setIsMeetingsEnabled(!isMeetingsEnabled);
    updateUserSettings.mutate(
      { calendarPermissionLevel },
      {
        onSuccess: () => {
          logger.debug("Successfully updated calendar permission level");
        },
        onError: (err) => {
          toast.error("Sorry, something went wrong. Please try again.");
          setIsMeetingsEnabled(isMeetingsEnabled);
          Sentry.captureException(err);
        },
      }
    );
  };

  return (
    <Wrapper>
      <Content>
        <Row spacing="16px">
          <Switch
            checked={isMeetingsEnabled}
            onChange={handleChange}
            disabled={isLoading}
            noMargin
          />
          <Column spacing="4px" onClick={handleChange}>
            <Typography variant="bodyPrimaryReg" color="shades.neutral.700">
              Add Read to my meetings
            </Typography>
            <Typography variant="bodySecondaryReg" color="shades.neutral.500">
              When you allow Read to join meetings, it can automatically take
              meeting notes for you and generate more accurate recommendations.
            </Typography>
          </Column>
        </Row>
        <Collapse in={isMeetingsEnabled}>
          <EditableSettings
            settings={PREVIEW_SETTINGS.map((setting) => ({
              ...setting,
              disableBorder: true,
            }))}
            isPreview
          />
        </Collapse>
      </Content>
      <Row variant="center">
        <Button variant="text" onClick={() => setModalOpen(true)}>
          Advanced Settings
        </Button>
      </Row>
      <AdvancedSettingsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Wrapper>
  );
};

export default CalendarStepSettingsRss;
